import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Ask Your Donors for a Support Increase (Without Feeling Guilty)';
const DESCRIPTION =
    "A practical guide to asking existing donors to raise their giving — when cost-of-living catches up with your support, how to frame the ask so it doesn't feel like a complaint, and a simple script that keeps the relationship intact either way.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover1.jpg',
            datePublished: '2026-07-30',
            dateModified: '2026-07-30',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-for-a-support-increase',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it okay to ask existing donors to increase their giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. Support that was set years ago rarely accounts for inflation or a growing family, and most donors expect the number to move over time — they just need you to actually ask instead of quietly absorbing the gap yourself.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do I ask for a support increase without sounding like a complaint?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Frame it around a specific, factual reason — a cost-of-living adjustment, a new ministry responsibility, a support level that hasn't changed since you started — rather than a general statement that money is tight. A specific reason reads as an update, not a complaint.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask every donor for a support increase at once?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. A single note to your entire list can feel like a mass fundraising appeal. It works better one relationship at a time, or in small batches, timed to something real for that donor — an anniversary of their first gift, or your annual update.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if a donor says no to a support increase?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Say thank you and change nothing else — their current gift still matters and the relationship shouldn't cool because of a no. Many donors who decline an increase will still be your most reliable supporters for years, so protect that far more than the extra amount.",
                    },
                },
            ],
        },
    ],
};

export default function HowToAskForASupportIncrease() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-for-a-support-increase' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-support-increase-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover1.jpg" alt="A missionary reviewing a monthly budget at a kitchen table" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries set their support level once, near the start, and never revisit it — even as rent, groceries, and travel costs quietly climb for years afterward. Asking existing donors to raise their giving feels different from the first ask, because it can feel like admitting the first number wasn't enough. It isn't a complaint. Handled well, it's a normal, expected part of a long-term support relationship.</p>

                    <h2>Why the ask feels harder the second time</h2>

                    <p>The first ask is framed as a beginning — a new missionary, a new page, a clear story. A support increase has no obvious hook, so it can feel like there's nothing to justify it beyond "costs went up," which can seem thin to say out loud. The fix isn't to avoid the ask — it's to find the specific, factual reason that makes it a normal update rather than an appeal.</p>

                    <h2>Find your actual reason first</h2>

                    <p>A vague "things are tight" ask is the hardest to send and the easiest to decline. A specific one is easier for you to write and easier for a donor to say yes to. Common, legitimate reasons:</p>

                    <ul>
                        <li><strong>Cost-of-living catch-up:</strong> your support level hasn't moved since you started, but rent and groceries have.</li>
                        <li><strong>A changed field:</strong> a new country, city, or ministry role with a different cost structure.</li>
                        <li><strong>A growing family:</strong> a child, a move to a bigger space, added health costs.</li>
                        <li><strong>An expanded role:</strong> more responsibility on the field that the original support level was never sized for.</li>
                    </ul>

                    <p>Naming the real reason up front does more work than any amount of careful wording around a vague one.</p>

                    <h2>Ask individually, not as a mass appeal</h2>

                    <p>A single message blasted to your entire list reads like a fundraising campaign, not a personal update between two people who already know each other. Support increases land better one relationship at a time, or in small batches — timed to something real for that donor, like the anniversary of their first gift or your annual update, rather than sent to everyone the same week for the same reason.</p>

                    <h2>A simple script</h2>

                    <p>"I wanted to give you an honest update. When you started supporting me at $[X]/month back in [year], that covered [what it covered then] — with [the real reason: rent where I live now / our family growing / cost-of-living], that same amount doesn't stretch as far anymore. Would you be able to consider moving to $[Y]/month? No pressure at all if now isn't the right time — I'm just as grateful for what you're already giving."</p>

                    <p>Notice what this script does: it states a fact, names a specific new number, and explicitly removes pressure. All three matter — the fact justifies the ask, the specific number makes it easy to say yes to, and the release valve keeps a "no" from feeling like a conflict.</p>

                    <h2>Make the increase itself effortless</h2>

                    <p>Once someone agrees, don't make them dig up a form or remember a login. A direct link to update their existing recurring gift — not a new signup, just an adjustment — removes the last bit of friction between a yes and the money actually moving. On <a href="/">Devotion Fund</a>, a recurring supporter can update their own monthly amount in a couple of taps, so your job ends at the ask.</p>

                    <h2>What to do when the answer is no</h2>

                    <p>Say thank you, and change nothing else about the relationship. A donor who declines an increase is still a donor — often one of your most reliable ones — and treating the no as a small disappointment rather than a rupture protects a gift you already have in the hope of a bigger one you don't. Most long-term supporters will consider an increase again down the road if you don't make the first no feel awkward.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it okay to ask for a support increase?</strong><br />
                    Yes — support levels set years ago rarely track inflation or life changes, and most donors expect to be asked eventually.</p>

                    <p><strong>How do I avoid sounding like I'm complaining?</strong><br />
                    Lead with a specific, factual reason instead of a general statement that money is tight.</p>

                    <p><strong>Should I ask everyone at once?</strong><br />
                    No — one relationship at a time, timed to something real for that donor, works better than a mass appeal.</p>

                    <p><strong>What if a donor says no?</strong><br />
                    Thank them and change nothing else — protect the existing relationship over the extra amount.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">How to Ask a One-Time Donor to Become a Monthly Supporter</a></li>
                        <li><a href="/resources/diversify-missionary-support-sources">How to Diversify Missionary Support So One Donor or Church Can't Sink Your Budget</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. This guide came out of a pattern we kept seeing: missionaries quietly absorbing years of rising costs rather than asking existing donors for anything more, long after the original number stopped making sense.</p>
                    <p><small><em>Last updated: 2026-07-30.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-for-a-support-increase" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
